import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { fetchRecommendations, trackEvent } from "@/lib/api";
import { Lightbulb, ArrowRight, Loader2, AlertTriangle, Clock, Bell } from "lucide-react";
import FeedbackBar from "@/components/FeedbackBar";

const SEVERITY_META = {
  high:   { Icon: AlertTriangle, tint: "bg-rose-50 text-rose-700 border-rose-200" },
  medium: { Icon: Clock,         tint: "bg-amber-50 text-amber-700 border-amber-200" },
  low:    { Icon: Bell,          tint: "bg-[var(--fcc-green-soft)] text-[var(--fcc-green)] border-[var(--fcc-border)]" },
};

export default function RecommendationsCard() {
  const [items, setItems] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchRecommendations()
      .then((r) => setItems(r.recommendations || []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <section className="fcc-card p-8 text-center text-[var(--fcc-muted)]">
        <Loader2 className="w-5 h-5 mx-auto animate-spin" />
      </section>
    );
  }
  if (!items || items.length === 0) return null;

  return (
    <section data-testid="recommendations-card" className="fcc-card p-6 sm:p-7">
      <header className="flex items-baseline justify-between mb-4">
        <div>
          <p className="label-overline flex items-center gap-2">
            <Lightbulb className="w-3 h-3" /> Smart recommendations
          </p>
          <h2 className="font-display text-2xl text-[var(--fcc-ink)] mt-1.5">What KidHQ thinks you should look at</h2>
        </div>
      </header>
      <ul className="space-y-2.5">
        {items.map((rec, idx) => {
          const meta = SEVERITY_META[rec.severity] || SEVERITY_META.low;
          const Icon = meta.Icon;
          const recKey = rec.kind || `rec-${idx}`;
          return (
            <li key={idx} data-testid={`recommendation-${rec.kind}`} className="space-y-1.5">
              <Link
                to={rec.deep_link || "#"}
                onClick={() => trackEvent("recommendation_click", { rec_key: recKey })}
                className={`flex items-start gap-3 rounded-xl border p-4 hover:opacity-90 transition ${meta.tint}`}
              >
                <Icon className="w-4 h-4 mt-0.5 shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-sm truncate">{rec.title}</p>
                  <p className="text-xs mt-1 opacity-90">{rec.reason}</p>
                  <p className="text-xs mt-1.5 font-semibold">→ {rec.action}</p>
                  <div className="flex items-center gap-2 mt-1.5 text-[10px] uppercase tracking-widest opacity-70">
                    {rec.child_name && <span>{rec.child_name}</span>}
                    <span>· {Math.round((rec.confidence || 0) * 100)}% confidence</span>
                  </div>
                </div>
                <ArrowRight className="w-4 h-4 mt-0.5 shrink-0" />
              </Link>
              <FeedbackBar
                surface="recommendation"
                targetId={recKey}
                compact
                className="pl-1"
              />
            </li>
          );
        })}
      </ul>
    </section>
  );
}
